import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ScoreChart from "./ScoreChart";
import PathwayComparison from "./PathwayComparison";
import SuccessLikelihood from "./SuccessLikelihood";
import { generatePDF } from "@/lib/pdf-generator";
import type { AssessmentScores } from "@/lib/assessment/scoring";

interface AssessmentDashboardProps {
  scores: AssessmentScores;
  pathwayId: string;
  pathways: {
    id: string;
    title: string;
    match: number;
    likelihood: number;
    scores: AssessmentScores;
    requirements: {
      met: string[];
      missing: string[];
    };
  }[];
  recommendations: string[];
}

const AssessmentDashboard = ({ scores, pathwayId, pathways, recommendations }: AssessmentDashboardProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  const handleDownload = async () => {
    try {
      setIsGenerating(true);
      const pdfBlob = await generatePDF({
        scores,
        pathways,
        recommendations,
        pathwayId
      });

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `eligibility-assessment-${pathwayId}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Report Downloaded",
        description: "Your assessment report has been downloaded successfully.",
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast({
        variant: "destructive",
        title: "Download Failed",
        description: "There was a problem generating your report. Please try again.",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Find the current pathway
  const currentPathway = pathways.find(p => p.id === pathwayId);
  if (!currentPathway) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Assessment Results</h2>
          <p className="text-muted-foreground">
            {currentPathway.title} Eligibility Assessment
          </p>
        </div>
        <Button
          onClick={handleDownload}
          disabled={isGenerating}
        >
          <Download className="mr-2 h-4 w-4" />
          {isGenerating ? "Generating..." : "Download Report"}
        </Button>
      </div>

      {/* Success Likelihood Card */}
      <SuccessLikelihood 
        likelihood={currentPathway.likelihood}
        pathwayTitle={currentPathway.title}
      />

      {/* Detailed Results */}
      <Tabs defaultValue="scores">
        <TabsList>
          <TabsTrigger value="scores">Score Analysis</TabsTrigger>
          <TabsTrigger value="pathways">Requirements</TabsTrigger>
          <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
        </TabsList>

        <TabsContent value="scores">
          <ScoreChart scores={scores} className="mt-6" />
        </TabsContent>

        <TabsContent value="pathways">
          <PathwayComparison pathways={[currentPathway]} />
        </TabsContent>

        <TabsContent value="recommendations">
          <Card>
            <CardHeader>
              <CardTitle>Recommendations</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-4">
                {recommendations.map((recommendation, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2" />
                    <p>{recommendation}</p>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AssessmentDashboard;